import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip,
  CircularProgress, Divider, LinearProgress,
  Avatar, List, ListItem, ListItemText, ListItemAvatar,
  Button, Popover,
  // recharts 도 Tooltip 을 내보낸다. 그냥 들여오면 뒤에 온 쪽이 이겨서
  // 차트 툴팁이 조용히 MUI 것으로 바뀐다. 이름을 달리해 섞이지 않게 한다.
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import InventoryIcon from '@mui/icons-material/Inventory';
import BuildIcon from '@mui/icons-material/Build';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';

import { PQCD, METRIC_HELP } from '../../lib/pqcdMetrics';
import { calcItemScore } from '../../lib/pqcdTrend';
import usePqcdData from '../../lib/usePqcdData';
import * as kpiApi from '../../lib/api/kpiReport';
import * as mfgApi from '../../lib/api/manufacturing';
import * as noticeApi from '../../lib/api/notice';
import NoticeModalContainer from '../../containers/dashboard/notice/NoticeModalContainer';
import PqcdGauge from '../../components/dashboard/PqcdGauge';

// ── 색상 헬퍼 ─────────────────────────────────────────────
// 상단 KPI 카드 5개는 더 이상 이 함수로 값 색을 정하지 않는다(Phase 3 이후 - 큰 값은 항상
// 기본 텍스트색이고, 좋음/나쁨은 화살표·증감률 텍스트에만 남긴다). 장비별 가동률(섹션4)은
// 여전히 임계값 색으로 카드를 구분해야 해서 남겨둔다.
const rateColor = (val, good = 90, warn = 70) => {
  if (val == null || val === '-') return '#9e9e9e';
  const n = Number(val);
  if (n >= good) return '#388e3c';
  if (n >= warn) return '#f57c00';
  return '#c62828';
};

// PQCD 게이지 sub-line용 숫자 포맷. null/NaN 은 전부 '-' 로 - 게이지 카드 자체가 이미
// value==null 이면 '-' 를 보여주므로, 아래 텍스트도 같은 원칙으로 맞춘다.
const fmtGaugeNum = (val, digits = 1) => (val == null || Number.isNaN(val) ? '-' : val.toFixed(digits));

// 카드/게이지 sub-line의 base 라벨. Phase 4(2026-10)부터 base 모드(baseMode, pqcdTrend.js
// chooseBaseline 결과)가 항목마다 다르므로, 화면 전역이 아니라 각 calcTrend 결과가 가진
// baseMode 값으로 그때그때 고른다. 재공재고(C)만 "월말 스냅샷" 평균이라 문구를 한 단어 더
// 붙인다(isWip).
const baselineLabel = (mode, isWip) => {
  if (mode === 'lastYear') return isWip ? '작년 월말 평균' : '작년 월평균';
  return isWip ? '3개월 월말 평균' : '3개월 월평균';
};

// 최근 12개월(이번달 포함) 의 'YYYYMM' 집합. 섹션 2(월별 추이 차트)·섹션 4(장비 현황)가
// "선택 연도" 대신 이 창으로 데이터를 거른다(Phase 4, 2026-10 - "기준 연도" 셀렉터 폐지에
// 맞춰 아래 차트들도 고정 1년 대신 굴러가는 최근 12개월로 바꿨다). pqcdTrend.js 의 월 계산과
// 달리 이 창은 base 계산에 쓰이지 않아(순수 표시용) 그 파일로 옮기지 않고 여기 둔다.
const getLast12MonthsSet = (today) => {
  const set = new Set();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    set.add(`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return set;
};

// 레이더 "핵심성과지표 스코어" 툴팁 - 점수뿐 아니라 현재/기준 원값도 같이 보여준다
// (사용자 승인 사항). 각 축의 단위가 서로 달라(개·%·개/시간) 공통 포맷을 쓰지 않고 그냥
// 숫자만 보여준다.
const ScoreTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const { subject, score, current, base } = payload[0].payload;
  const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid #ddd', borderRadius: 1, p: 1, boxShadow: 1 }}>
      <Typography variant="caption" fontWeight={700} display="block">{subject}</Typography>
      <Typography variant="caption" display="block">점수 {score.toFixed(1)}점</Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        현재 {fmt(current)} / 기준 {fmt(base)}
      </Typography>
    </Box>
  );
};

// ── KPI 요약 카드 ─────────────────────────────────────────
/**
 * 지표 설명 말풍선.
 *
 * 무엇을 재는지, 어떤 식으로 나온 값인지를 적는다. 계산식은 실제로 도는 식을 그대로 쓴다 —
 * 교과서 정의를 적어두면 화면 숫자와 안 맞을 때 원인을 못 찾는다.
 */
const MetricHelp = ({ help }) => {
  if (!help) return null;
  const cat = PQCD[help.category];
  return (
    <MuiTooltip
      arrow
      placement="top"
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      title={
        <Box sx={{ py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>
            [{cat.key}] {cat.label} · {help.title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
            {help.desc}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.85 }}>
            계산식 : {help.formula}
          </Typography>
          {help.note && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.85 }}>
              {help.note}
            </Typography>
          )}
        </Box>
      }
    >
      <HelpOutlineIcon
        sx={{
          fontSize: 14, ml: 0.5, verticalAlign: 'middle',
          color: 'text.disabled', cursor: 'help',
          '&:hover': { color: 'text.secondary' },
        }}
      />
    </MuiTooltip>
  );
};

// category(P/Q/C/D) 색은 왼쪽 테두리 5px + 아이콘 배경에만 쓴다 - 큰 값 자체는 항상 기본
// 텍스트색이다(좋음/나쁨은 sub 의 화살표·증감률 텍스트에만 있다, buildCardView 참고).
// valueColor 는 "데이터 없음" 류의 회색 상태만 넘겨받는다(그 외엔 항상 undefined).
const KpiCard = ({ icon, label, value, unit, valueColor, category, sub, help }) => {
  const catColor = PQCD[category]?.color || '#616161';
  return (
    <Card
      variant="outlined"
      sx={{ height: '100%', position: 'relative', overflow: 'visible', borderLeft: `5px solid ${catColor}` }}
    >
      <Box sx={{
        position: 'absolute', top: -14, left: 16,
        bgcolor: catColor, borderRadius: 2, p: 1,
        boxShadow: `0 4px 12px ${catColor}66`,
      }}>
        {icon}
      </Box>
      <CardContent sx={{ pt: 3.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {label}
          <MetricHelp help={help} />
        </Typography>
        {/* valueColor 는 "데이터 없음"류 안내문일 때만 온다 - 숫자 크기(h4)로 찍으면 문구가 카드를
            넘치고 실제 값처럼 도드라져 보여서, 안내문은 한 단계 작게(h6) 둔다. */}
        <Typography
          variant={valueColor ? 'h6' : 'h4'}
          fontWeight={800}
          sx={{ color: valueColor || 'text.primary', lineHeight: valueColor ? 1.9 : 1.1 }}
        >
          {value}
          <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>
            {unit}
          </Typography>
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ── 제조계획 상태 Chip ─────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    미시작: { label: '미시작', color: 'default' },
    설계중: { label: '설계중', color: 'info' },
    생산중: { label: '생산중', color: 'warning' },
    출하대기: { label: '출하대기', color: 'info' },
    출하중: { label: '출하중', color: 'primary' },
    완료: { label: '완료', color: 'success' },
    종결: { label: '종결', color: 'error' },
    IN_PROGRESS: { label: '진행중', color: 'primary' },
    COMPLETED: { label: '완료', color: 'success' },
    PENDING: { label: '대기', color: 'default' },
    CANCELLED: { label: '취소', color: 'error' },
  };
  const s = map[status] || { label: status, color: 'default' };
  return <Chip label={s.label} color={s.color} size="small" />;
};

// ── 색상 안내 팝오버 ───────────────────────────────────────
// 항목 색(PQCD, 어느 항목인지)과 상태 색(좋다/나쁘다)이 서로 다른 용도임을 한 번에 보여준다.
const ColorLegendPopover = ({ anchorEl, onClose }) => (
  <Popover
    open={!!anchorEl}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  >
    <Box sx={{ p: 2, maxWidth: 280 }}>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>
        항목 색 (어느 항목인지)
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        {Object.values(PQCD).map((c) => (
          <Chip
            key={c.key}
            label={`${c.key} ${c.label}`}
            size="small"
            sx={{ bgcolor: c.color, color: '#fff', fontWeight: 600 }}
          />
        ))}
      </Box>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>
        상태 색 (좋다/나쁘다)
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {[
          { label: '좋아짐', color: '#2e7d32' },
          { label: '주의', color: '#f57c00' },
          { label: '나빠짐', color: '#d32f2f' },
          { label: '데이터 없음', color: '#9e9e9e' },
        ].map((s) => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: s.color, borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Popover>
);

// ── 메인 대시보드 ─────────────────────────────────────────
const MainDashboardPage = () => {
  const navigate = useNavigate();
  const now = new Date();

  // 데이터 상태 (섹션 2 월별 추이 차트 + 섹션 4 장비현황 전용 - 최근 12개월 롤링 윈도우,
  // Phase 4(2026-10) 부터 "기준 연도" 셀렉터가 없어져 고정 1년 대신 이 창을 쓴다).
  const [production,  setProduction]  = useState([]);
  const [quality,     setQuality]     = useState([]);
  const [processDefect, setProcessDefect] = useState([]);
  const [eqpCost,     setEqpCost]     = useState([]);
  const [plans,       setPlans]       = useState([]);
  const [notices,     setNotices]     = useState([]);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [loadingKpi,  setLoadingKpi]  = useState(true);
  const [loadingOps,  setLoadingOps]  = useState(true);
  const [colorInfoAnchor, setColorInfoAnchor] = useState(null); // '색상 안내' 팝오버

  // PQCD 게이지 + 상단 KPI 카드 5개 + "핵심성과지표 스코어" 레이더 전용 데이터.
  // Phase 4(2026-10)부터 공용 훅(usePqcdData)으로 뺐다 - KPI 리포트 대시보드 탭도 같은
  // 자동 베이스라인 계산이 필요해져, 페이지마다 복붙하면 숫자가 다시 갈릴 수 있었다.
  const {
    loading: loadingPqcd,
    pqcdThroughput, pqcdDefect, pqcdWipResult, pqcdDeliveryShorten,
    cardProduced, cardOtd,
  } = usePqcdData();

  // KPI 로드 (섹션 2 차트 + 섹션 4 장비현황 전용 - 최근 12개월 롤링 윈도우). recent3 와 같은
  // 이유로 이 창은 올해 아니면 작년만 걸친다 - 그래서 두 해만 불러 합친 뒤 last12 로 거른다.
  useEffect(() => {
    setLoadingKpi(true);
    const today = new Date();
    const thisY = today.getFullYear();
    const lastY = thisY - 1;
    const last12 = getLast12MonthsSet(today);
    const filterLast12 = (list) => (list || []).filter((r) => last12.has(r.kpiYm));

    Promise.allSettled([
      kpiApi.getProductionKpi({ searchYear: thisY }),
      kpiApi.getProductionKpi({ searchYear: lastY }),
      kpiApi.getQualityKpi({ searchYear: thisY }),
      kpiApi.getQualityKpi({ searchYear: lastY }),
      kpiApi.getProcessDefectKpi({ searchYear: thisY }),
      kpiApi.getProcessDefectKpi({ searchYear: lastY }),
      kpiApi.getEqpCostKpi({ searchYear: thisY }),
      kpiApi.getEqpCostKpi({ searchYear: lastY }),
    ]).then(([pT, pL, qT, qL, pdT, pdL, eT, eL]) => {
      setProduction(filterLast12([...(pT.value?.data?.list || []), ...(pL.value?.data?.list || [])]));
      setQuality(filterLast12([...(qT.value?.data?.list || []), ...(qL.value?.data?.list || [])]));
      setProcessDefect(filterLast12([...(pdT.value?.data?.list || []), ...(pdL.value?.data?.list || [])]));
      setEqpCost(filterLast12([...(eT.value?.data?.list || []), ...(eL.value?.data?.list || [])]));
    }).finally(() => setLoadingKpi(false));
  }, []);

  // 운영 현황 로드 (연도 무관)
  useEffect(() => {
    setLoadingOps(true);
    Promise.allSettled([
      mfgApi.getProjectsWithSummary({ page: 1, size: 6, status: '진행중' }),
      noticeApi.getDashNotices({ page: 0, size: 6 }),
    ]).then(([p, n]) => {
      setPlans  (p.value?.data?.data?.content || p.value?.data?.list || []);
      const nd = n.value?.data;
      setNotices(Array.isArray(nd) ? nd : nd?.list || nd?.content || []);
    }).finally(() => setLoadingOps(false));
  }, []);

  // ── 월별 추이 (생산 + 공정불량, 최근 12개월) ───────────────────────────────
  const monthlyMap = {};
  production.forEach((r) => {
    const ym = r.kpiYm; if (!ym) return;
    if (!monthlyMap[ym]) monthlyMap[ym] = { ym };
    monthlyMap[ym].produced = (monthlyMap[ym].produced || 0) + (Number(r.producedQty) || 0);
  });
  // 불량수량은 production(생산 KPI)의 defectQty 가 아니라 processDefect(검사 공정 KPI)의
  // processDefectQty 로 낸다 - production.defectQty 는 입력 화면이 없어 개발DB에서 항상 0이라
  // 그대로 쓰면 차트가 늘 "불량 0" 으로 보인다.
  processDefect.forEach((r) => {
    const ym = r.kpiYm; if (!ym) return;
    if (!monthlyMap[ym]) monthlyMap[ym] = { ym };
    monthlyMap[ym].defect = (monthlyMap[ym].defect || 0) + (Number(r.processDefectQty) || 0);
  });
  quality.forEach((r) => {
    const ym = r.kpiYm; if (!ym) return;
    if (!monthlyMap[ym]) monthlyMap[ym] = { ym };
    monthlyMap[ym].failQty = (monthlyMap[ym].failQty || 0) + (Number(r.failQty) || 0);
  });
  // 실적이 없는 달도 0 으로 칸을 채운다 - 있는 달만 그리면 축이 띄엄띄엄(6월만 덩그러니 등)
  // 나와서 "최근 12개월" 이라는 제목과 안 맞고, 빈 달이 빈 달로 보이지도 않는다.
  const monthlyData = [...getLast12MonthsSet(new Date())].sort().map((ym) => ({
    ym, produced: 0, defect: 0, failQty: 0, ...monthlyMap[ym],
  }));

  // ── 핵심성과지표 스코어 레이더 (Phase 4, 2026-10 - 기존 "KPI 종합 스코어"를 대체) ──
  // 축은 상단 KPI 카드 5개와 완전히 같다. base=100점 기준 calcItemScore 로 점수를 낸다
  // (pqcdTrend.js 참고) - current/base 중 하나라도 없으면(또는 higher-better인데 base=0)
  // null 이 와서 0점 + 축 이름에 "(데이터 없음)" 을 붙인다.
  const scoreItems = [
    { subject: '생산완료수량', current: cardProduced.current, base: cardProduced.base, higherIsBetter: true },
    { subject: '시간당 생산량', current: pqcdThroughput.current, base: pqcdThroughput.base, higherIsBetter: true },
    { subject: '완제품 불량률', current: pqcdDefect.current, base: pqcdDefect.base, higherIsBetter: false },
    { subject: '재공재고', current: pqcdWipResult.current, base: pqcdWipResult.base, higherIsBetter: false },
    { subject: '제조납기준수율', current: cardOtd.current, base: cardOtd.base, higherIsBetter: true },
  ];
  const scoreRadarData = scoreItems.map(({ subject, current, base, higherIsBetter }) => {
    const rawScore = calcItemScore(current, base, higherIsBetter);
    const noData = rawScore == null;
    return {
      subject: noData ? `${subject} (데이터 없음)` : subject,
      score: noData ? 0 : rawScore,
      ref: 100,
      current, base, noData,
    };
  });

  // ── 장비별 이번달 가동률 (최근 12개월 평균) ──────────────────────────────────
  const eqpSummary = React.useMemo(() => {
    const map = {};
    eqpCost.forEach((r) => {
      const k = r.eqpNm || r.eqpUuid;
      if (!k) return;
      if (!map[k]) map[k] = { eqpNm: k, operRateSum: 0, mtbfSum: 0, cnt: 0 };
      map[k].operRateSum += Number(r.operRate) || 0;
      map[k].mtbfSum     += Number(r.mtbf)     || 0;
      map[k].cnt         += 1;
    });
    return Object.values(map).map((r) => ({
      eqpNm:    r.eqpNm,
      operRate: r.cnt ? +(r.operRateSum / r.cnt).toFixed(1) : 0,
      mtbf:     r.cnt ? +(r.mtbfSum    / r.cnt).toFixed(1) : 0,
    }));
  }, [eqpCost]);

  const handleCloseNoticeModal = () => {
    setSelectedNotice(null);
  };

  // 게이지 섹션 부제 - Phase 4부터 base 가 항목마다 자동으로 달라, 패널 부제는 "항목별"이라고
  // 통째로 설명하고 각 카드/게이지 sub-line 이 실제로 쓴 base 모드를 baselineLabel 로 보여준다.
  const pqcdMonthStr = String(now.getMonth() + 1).padStart(2, '0');
  const pqcdDayStr   = String(now.getDate()).padStart(2, '0');
  const pqcdCurrentRangeLabel = `${pqcdMonthStr}.01~${pqcdMonthStr}.${pqcdDayStr}`;

  /**
   * 상단 KPI 카드 5개 공통 표시 로직. calcTrend 계열 결과({current, base, baseMonthCount,
   * baseMode, pct, baseZero})를 KpiCard 의 {value, unit, valueColor, sub} 로 바꾼다.
   *
   * 데이터 없음 상태 3가지(사용자 승인 사항, 순서대로 처리해야 겹치지 않는다):
   *   1) current 도 base 도 없음 → 값 자리에 회색 "데이터 없음" (단위 숨김)
   *   2) current 만 없음        → 값 자리에 회색 "이번달 실적 없음" (단위 숨김), sub 엔 base 는 보여준다
   *   3) base 만 없음(current 는 있음) → 값은 정상 표시, sub 엔 "비교 데이터 없음"
   * base 가 0(비율 계산 불가)이면 화살표/증감률 없이 "왜 비교를 못 하는지"만 적는다 -
   * 기존 Q 게이지의 "기준 불량률 0%" 메시지와 같은 취지를 카드에도 일반화한 것이다.
   * baseLabel 은 더 이상 호출부가 고정으로 넘기지 않는다 - result.baseMode(chooseBaseline이
   * 그 항목에 자동으로 고른 모드)로 baselineLabel 이 그때그때 고른다(isWip 만 호출부가 알려준다).
   */
  const buildCardView = (result, { unit, higherIsBetter, formatNum, isWip }) => {
    const { current, base, baseMonthCount, baseMode, pct, baseZero } = result;
    const noData = current == null;
    const baseLabel = baselineLabel(baseMode, isWip);

    let value;
    let valueColor = null; // null 이면 호출부가 지정한 기존 색(임계값 색상 함수 등)을 그대로 쓴다
    if (noData && base == null) {
      value = '데이터 없음';
      valueColor = '#9e9e9e';
    } else if (noData) {
      value = '이번달 실적 없음';
      valueColor = '#9e9e9e';
    } else {
      value = formatNum(current);
    }

    let sub = null;
    if (base == null) {
      if (!noData) sub = '비교 데이터 없음';
      // current·base 둘 다 없으면 sub 없이 값 자리 메시지만으로 충분하다.
    } else if (baseZero) {
      sub = `${baseLabel} ${formatNum(base)}${unit} (${baseMonthCount}개월) — 변화율 계산 불가(기준 0)`;
    } else {
      const baseText = `${baseLabel} ${formatNum(base)}${unit} (${baseMonthCount}개월)`;
      if (pct == null) {
        // current 가 없어 증감률 자체를 못 낸다(위 2번 상태) - base 정보만 보여준다.
        sub = baseText;
      } else {
        const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '―';
        const better = pct === 0 ? null : (higherIsBetter ? pct > 0 : pct < 0);
        const color = better == null ? '#9e9e9e' : (better ? '#2e7d32' : '#d32f2f');
        sub = (
          <>
            {baseText}{' '}
            <Box component="span" sx={{ color, fontWeight: 700 }}>
              {arrow}{Math.abs(pct).toFixed(1)}%
            </Box>
          </>
        );
      }
    }

    return { value, unit: noData ? '' : unit, valueColor, sub };
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* ── 헤더 ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>종합 운영 대시보드</Typography>
          <Typography variant="body2" color="text.secondary">
            {now.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
          </Typography>
        </Box>
      </Box>

      {/* ── 섹션 1: KPI 요약 카드 + PQCD 게이지 (Phase 3, Phase 4 2026-10 베이스라인 자동화) ──
          값은 항상 "이번달"이고, 비교 기준(base)은 더는 셀렉터가 아니라 항목마다 자동으로
          고른다(usePqcdData/chooseBaseline) - 그래서 이 블록은 loadingKpi(차트·장비현황
          전용, 아래 별도 이펙트)가 아니라 loadingPqcd 를 따른다.
          카드 5개 + 게이지 4개를 패널 하나로 묶고, 제목/비교기준 부제/색상 안내 버튼은
          패널 헤더에 한 번만 둔다(예전엔 게이지 쪽에 "PQCD 핵심지표" 제목이 따로 있었다). */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>핵심성과지표</Typography>
              <Typography variant="caption" color="text.secondary">
                비교: 항목별 — 작년 월평균(작년 데이터가 없으면 직전 3개월 월평균) / 이번달: {pqcdCurrentRangeLabel}
              </Typography>
            </Box>
            <Button
              size="small" variant="outlined" startIcon={<PaletteOutlinedIcon fontSize="small" />}
              onClick={(e) => setColorInfoAnchor(e.currentTarget)}
            >
              색상 안내
            </Button>
            <ColorLegendPopover anchorEl={colorInfoAnchor} onClose={() => setColorInfoAnchor(null)} />
          </Box>

          {loadingPqcd ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* 10개 중 5개만 남긴다(양품률·공정불량률·인력평균활용률·설비평균가동률·제조리드타임은
                  KPI 리포트 화면에서 본다). 12열로는 5등분이 안 되어 columns 를 직접 지정한다
                  (모바일 2 / 태블릿 3 / 데스크톱 5). */}
              <Grid container spacing={3} columns={{ xs: 4, sm: 12, md: 15 }} sx={{ mb: 3, mt: 1.5 }}>
            <Grid item xs={2} sm={4} md={3}>
              {(() => {
                const v = buildCardView(cardProduced, {
                  unit: '개', higherIsBetter: true,
                  formatNum: (n) => (n == null ? '-' : Math.round(n).toLocaleString()),
                });
                return (
                  <KpiCard
                    icon={<InventoryIcon sx={{ color: '#fff', fontSize: 20 }} />}
                    label="이번달 생산완료수량"
                    value={v.value} unit={v.unit}
                    category="P"
                    valueColor={v.valueColor}
                    sub={v.sub}
                    help={METRIC_HELP.producedQty}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={2} sm={4} md={3}>
              {(() => {
                // 게이지 P(pqcdThroughput)와 같은 월별 값을 공유한다(pqcdTrend.js calcThroughputTrend 주석 참고).
                const v = buildCardView(pqcdThroughput, {
                  unit: '개/시간', higherIsBetter: true,
                  formatNum: (n) => (n == null ? '-' : n.toFixed(2)),
                });
                return (
                  <KpiCard
                    icon={<BoltIcon sx={{ color: '#fff', fontSize: 20 }} />}
                    label="이번달 시간당 생산량"
                    value={v.value} unit={v.unit}
                    category="P"
                    valueColor={v.valueColor}
                    sub={v.sub}
                    help={METRIC_HELP.throughput}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={2} sm={4} md={3}>
              {(() => {
                // 게이지 Q(pqcdDefect)와 같은 월별 값을 공유한다(pqcdTrend.js calcFinalDefectTrend 주석 참고).
                const v = buildCardView(pqcdDefect, {
                  unit: '%', higherIsBetter: false,
                  formatNum: (n) => (n == null ? '-' : n.toFixed(2)),
                });
                return (
                  <KpiCard
                    icon={<FactCheckIcon sx={{ color: '#fff', fontSize: 20 }} />}
                    label="이번달 완제품 불량률"
                    value={v.value} unit={v.unit}
                    category="Q"
                    valueColor={v.valueColor}
                    sub={v.sub}
                    help={METRIC_HELP.finalDefectRateCard}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={2} sm={4} md={3}>
              {(() => {
                // 게이지 C(pqcdWipResult)와 같은 값을 공유한다(pqcdTrend.js calcWipTrend 주석 참고) -
                // base 라벨도 게이지와 같은 "월말 스냅샷" 문구(isWip)를 쓴다.
                const v = buildCardView(pqcdWipResult, {
                  unit: '개', higherIsBetter: false, isWip: true,
                  formatNum: (n) => (n == null ? '-' : Math.round(n).toLocaleString()),
                });
                return (
                  <KpiCard
                    icon={<WarehouseIcon sx={{ color: '#fff', fontSize: 20 }} />}
                    label="이번달(오늘) 재공재고"
                    value={v.value} unit={v.unit}
                    category="C"
                    valueColor={v.valueColor}
                    sub={v.sub}
                    help={METRIC_HELP.wipQtyCard}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={2} sm={4} md={3}>
              {(() => {
                const v = buildCardView(cardOtd, {
                  unit: '%', higherIsBetter: true,
                  formatNum: (n) => (n == null ? '-' : n.toFixed(1)),
                });
                return (
                  <KpiCard
                    icon={<TrendingUpIcon sx={{ color: '#fff', fontSize: 20 }} />}
                    label="이번달 제조납기준수율"
                    value={v.value} unit={v.unit}
                    category="D"
                    valueColor={v.valueColor}
                    sub={v.sub}
                    help={METRIC_HELP.otdRate}
                  />
                );
              })()}
            </Grid>
          </Grid>

          {/* ── PQCD 변화율 게이지 (Phase 3 - KPI 카드 바로 아래, 차트보다 위) ──
              카드와 같은 자동 베이스라인(usePqcdData/chooseBaseline)을 쓴다. 제목/부제는
              위 패널 헤더로 옮겨 여기서는 생략한다. */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <PqcdGauge
                    title="시간당 생산량"
                    category="P"
                    value={pqcdThroughput.gauge}
                    unit="%"
                    min={0} max={200} reference={100}
                    higherIsBetter
                    lines={[
                      `이번달 ${fmtGaugeNum(pqcdThroughput.current, 2)} 개/시간`,
                      `${baselineLabel(pqcdThroughput.baseMode)} ${fmtGaugeNum(pqcdThroughput.base, 2)} 개/시간 (${pqcdThroughput.baseMonthCount}개월)`,
                    ]}
                    help={METRIC_HELP.throughputTrend}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <PqcdGauge
                    title="완제품 불량률"
                    category="Q"
                    value={pqcdDefect.gauge}
                    unit="%"
                    min={0} max={200} reference={100}
                    higherIsBetter={false}
                    lines={[
                      `이번달 ${fmtGaugeNum(pqcdDefect.current, 2)}% (${pqcdDefect.currentCount ?? 0}건)`,
                      pqcdDefect.baseZero
                        ? '기준 불량률 0%'
                        : `${baselineLabel(pqcdDefect.baseMode)} ${fmtGaugeNum(pqcdDefect.base, 2)}% (${pqcdDefect.baseMonthCount}개월)`,
                    ]}
                    help={METRIC_HELP.finalDefectTrend}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <PqcdGauge
                    title="재공재고율"
                    category="C"
                    value={pqcdWipResult.gauge}
                    unit="%"
                    min={0} max={200} reference={100}
                    higherIsBetter={false}
                    lines={
                      pqcdWipResult.current == null && pqcdWipResult.base == null
                        // 스냅샷 요청이 전부 실패했거나, 그 구간 내내 계획이 하나도 없었을 때만
                        // (existPlanCnt=0) 여기로 온다 - 0 재공은 정상 값이라 이 분기를 안 탄다.
                        ? ['재공 데이터 없음']
                        : [
                          `이번달(오늘) 재공 ${fmtGaugeNum(pqcdWipResult.current, 1)}개 (${pqcdWipResult.currentCount ?? 0}건)`,
                          `${baselineLabel(pqcdWipResult.baseMode, true)} ${fmtGaugeNum(pqcdWipResult.base, 1)}개`,
                        ]
                    }
                    help={METRIC_HELP.wipTrend}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <PqcdGauge
                    title="납기 단축률"
                    category="D"
                    value={pqcdDeliveryShorten.gauge}
                    unit="%"
                    min={-50} max={50} reference={0}
                    higherIsBetter
                    lines={[
                      `이번달 ${fmtGaugeNum(pqcdDeliveryShorten.current, 1)}일 (${pqcdDeliveryShorten.currentCount ?? 0}건)`,
                      `${baselineLabel(pqcdDeliveryShorten.baseMode)} ${fmtGaugeNum(pqcdDeliveryShorten.base, 1)}일 (${pqcdDeliveryShorten.baseMonthCount}개월)`,
                    ]}
                    help={METRIC_HELP.deliveryShortenRate}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── 섹션 2: 차트 (기존 그대로 - 선택 연도 단일 실적, loadingKpi) ── */}
      {loadingKpi ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* 월별 추이 */}
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ height: 300 }}>
              <CardContent sx={{ height: '100%', pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>월별 생산완료 · 공정불량 추이</Typography>
                  <Chip
                    label="생산 리포트 →"
                    size="small"
                    onClick={() => navigate('/kpimng?tab=production')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={monthlyData} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend iconSize={10} />
                    <Bar dataKey="produced" name="생산완료수량" fill={PQCD.P.color} barSize={12} />
                    <Bar dataKey="defect"   name="검사공정 불량수량" fill={PQCD.Q.color} barSize={12} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* 핵심성과지표 스코어 레이더 (Phase 4, 2026-10 - 기존 "KPI 종합 스코어" 대체) ──
              축은 상단 카드 5개와 같다. 점 100점짜리 기준선(회색 점선)과 실제 점수(파란
              실선) 두 겹을 그려 "기준 대비 지금 어디 있나"를 한눈에 보게 한다. */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: 300 }}>
              <CardContent sx={{ height: '100%', pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>핵심성과지표 스코어</Typography>
                  <Chip
                    label="KPI 리포트 →"
                    size="small"
                    onClick={() => navigate('/kpimng')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
                <ResponsiveContainer width="100%" height="85%">
                  <RadarChart data={scoreRadarData} margin={{ top: 0, right: 20, bottom: 0, left: 20 }}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9 }} />
                    <PolarRadiusAxis domain={[0, 150]} tick={{ fontSize: 9 }} />
                    <Radar name="기준선" dataKey="ref" stroke="#9e9e9e" strokeDasharray="4 4" fill="none" />
                    <Radar name="점수" dataKey="score" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Tooltip content={<ScoreTooltip />} />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── 섹션 3: 운영 현황 ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* 제조 계획 현황 */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>진행 중 제조계획</Typography>
                </Box>
                <Chip
                  label="전체 보기 →"
                  size="small"
                  onClick={() => navigate('/manufacturingmng')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <Divider sx={{ mb: 1 }} />
              {loadingOps ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : plans.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  진행 중인 제조계획이 없습니다.
                </Typography>
              ) : (
                <Box>
                  {plans.slice(0, 6).map((plan, i) => (
                    <Box
                      key={plan.projectId || i}
                      sx={{
                        py: 1, px: 0.5,
                        borderBottom: i < plans.length - 1 ? '1px solid #f0f0f0' : 'none',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                        borderRadius: 1,
                      }}
                      onClick={() => plan.projectId && navigate(`/manufacturingmng/${plan.projectId}/plans`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {plan.projectName || plan.projectNumber || '제조계획'}
                          </Typography>
                          {plan.customerName && (
                            <Typography variant="caption" color="text.secondary">{plan.customerName}</Typography>
                          )}
                        </Box>
                        <StatusChip status={plan.manufacturingStatus} />
                      </Box>
                      {plan.overallProgress != null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Number(plan.overallProgress) || 0}
                            sx={{ flex: 1, height: 5, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32 }}>
                            {plan.overallProgress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 공지사항 */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnnouncementIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>공지사항</Typography>
                </Box>
                <Chip
                  label="전체 보기 →"
                  size="small"
                  onClick={() => navigate('/noticemng')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <Divider sx={{ mb: 1 }} />
              {loadingOps ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : notices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  공지사항이 없습니다.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {notices.slice(0, 6).map((notice, i) => (
                    <ListItem
                      key={notice.wrUuid || notice.id || i}
                      disablePadding={false}
                      onClick={() => setSelectedNotice(notice)}
                      sx={{
                        borderBottom: i < notices.length - 1 ? '1px solid #f0f0f0' : 'none',
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#e3f2fd', color: '#1976d2', fontSize: 12 }}>
                          공
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {notice.wrTitle || notice.title || '공지'}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {notice.crtNm && `${notice.crtNm} · `}
                            {notice.crtDt ? new Date(notice.crtDt).toLocaleDateString('ko-KR') : ''}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── 섹션 4: 장비 현황 ── */}
      {eqpSummary.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>장비별 가동률 현황 (최근 12개월 평균)</Typography>
              </Box>
              <Chip
                label="원가 리포트 →"
                size="small"
                onClick={() => navigate('/kpimng?tab=cost')}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
            <Grid container spacing={1.5}>
              {eqpSummary.map((eqp) => {
                const color = rateColor(eqp.operRate, 80, 60);
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={eqp.eqpNm}>
                    <Card
                      variant="outlined"
                      sx={{ bgcolor: '#fafafa', borderColor: `${color}55` }}
                    >
                      <CardContent sx={{ py: 1.2, '&:last-child': { pb: 1.2 }, px: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" noWrap display="block" fontWeight={600}>
                          {eqp.eqpNm}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.3 }}>
                          <Typography variant="h6" fontWeight={800} sx={{ color }}>
                            {eqp.operRate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, eqp.operRate)}
                          sx={{
                            mt: 0.5, height: 4, borderRadius: 2,
                            bgcolor: `${color}22`,
                            '& .MuiLinearProgress-bar': { bgcolor: color },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                          MTBF {eqp.mtbf}h
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      <NoticeModalContainer
        notice={selectedNotice}
        open={!!selectedNotice}
        handleClose={handleCloseNoticeModal}
      />
    </Box>
  );
};

export default MainDashboardPage;
